import type { JSONContent } from '@tiptap/react';
import { ModalService } from 'src/frontend/services/ModalService';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { JobHelpers } from '@diagram/helpers/JobHelpers';
import {
    COMPONENT_INSTANCE_MENTION_TYPE,
    componentInstanceMentionId,
} from '@diagram/hooks/use-component-instance-mentions';
import { ChatChannelAccessService } from '@diagram/services/ChatChannelAccessService';
import { ChatService } from '@diagram/services/ChatService';
import { SidebarType } from '@diagram/state/sidebar';

import {
    DepSupportChannelType,
    DiagramChatChannelType,
    DiagramComponentInstance,
    JobStatus,
    JobType,
    Zone,
    ZoneValidationError,
} from 'models';
import { ChatHelpers } from 'helpers/ChatHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { state as currentProjectState } from 'state/current-project';
import { currentTeamCompaniesState } from 'state/current-team-companies';
import { state as currentUserState } from 'state/current-user';

import { SidebarService } from './SidebarService';

type SimulationJob = {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    error?: string | number | boolean | unknown[] | { [k: string]: unknown } | null;
};

const SupportService = {
    generateSimulationMessage: (type: JobType, job: SimulationJob): JSONContent => {
        const typeLabel = JobHelpers.getJobTypeLabel(type).toLowerCase();
        const createdDate = new Date(job.createdAt).toLocaleString();

        return doc(
            paragraph(
                text(
                    job.status === JobStatus.DONE
                        ? `Hi, could you help me interpret the results of my ${typeLabel} simulation from ${createdDate}?`
                        : `Hi, my ${typeLabel} simulation from ${createdDate} failed. Could you take a look?`,
                ),
            ),
            paragraph(
                text('Name:', true),
                text(` ${job.name}`),
                { type: 'hardBreak' },
                text('ID:', true),
                text(` ${job.id}`),
            ),
        );
    },

    generateValidationErrorsMessage: (zoneType: Zone['type'], errors: ZoneValidationError[] = []): JSONContent => {
        return doc(paragraph(text('I am running into validation errors. Please assist.')));
    },

    generateProductSearchMessage: (
        componentInstance?: Pick<DiagramComponentInstance, 'id' | 'designator'> | null,
    ): JSONContent => {
        const intro = 'Hi, I could not find a product that matches all my requirements';

        return doc(
            paragraph(
                ...(componentInstance
                    ? [
                          text(`${intro} for component `),
                          {
                              type: COMPONENT_INSTANCE_MENTION_TYPE,
                              attrs: {
                                  id: componentInstanceMentionId(componentInstance.id),
                                  label: componentInstance.designator,
                              },
                          },
                          text('.'),
                      ]
                    : [text(`${intro} in the product search.`)]),
            ),
            paragraph(text('Could you help me find a suitable product?')),
        );
    },

    openSupportChannel: (initialMessage?: string | object, trackingLocation?: string) =>
        ChatService.whileConnecting({}, async (isCancelled) => {
            const project = currentProjectState.project!;
            const user = currentUserState.user!;

            const { channels } = await ChatService.getChannels(project.id);

            if (isCancelled()) {
                return;
            }

            const prefill = () => {
                if (initialMessage) {
                    ChatService.prefillMessage(initialMessage);

                    InternalTrackingService.track('diagram.chat.support.message-prefilled', {
                        location: trackingLocation,
                    });
                }
            };

            const existing = ChatHelpers.findDefaultChannel(channels, DiagramChatChannelType.ENGINEERING_SUPPORT);

            if (existing) {
                prefill();
                ChatService.activateChannel(existing.id, existing.type);

                return;
            }

            // Team members share one #engineering-support channel for the whole team.
            if (project.permissionsAs === 'user') {
                prefill();

                await SupportService.createDepSupportChannel(
                    project.id,
                    DiagramChatChannelType.ENGINEERING_SUPPORT,
                    isCancelled,
                );

                return;
            }

            // Collaborators get a personal channel, named after them.
            if (user && project.permissionsAs === 'collaborator') {
                const personal = channels.find(
                    (channel) =>
                        channel.type === DiagramChatChannelType.COLLABORATOR &&
                        CollectionHelpers.equals(channel.createdBy, user.id),
                );

                prefill();

                if (personal) {
                    ChatService.activateChannel(personal.id, personal.type);

                    return;
                }

                const channel = await ChatService.createChannel(
                    project.id,
                    ChatHelpers.transformChannelName(user.name!),
                    DiagramChatChannelType.COLLABORATOR,
                );

                await ChatChannelAccessService.addUser(channel, user.id);

                if (isCancelled()) {
                    return;
                }

                ChatService.activateChannel(channel.id, channel.type);

                return;
            }

            // Company users (OEMs, manufacturers, ...) go to the channel their company was invited to.
            if (project.permissionsAs === 'company') {
                const companyIds = CollectionHelpers.ids(currentTeamCompaniesState.companies);
                const channelCompanies = await ChatChannelAccessService.listCompanies(project.id);

                if (isCancelled()) {
                    return;
                }

                const companyChannel = ChatHelpers.findCompanyChannel(channels, channelCompanies, companyIds);

                if (companyChannel) {
                    prefill();
                    ChatService.activateChannel(companyChannel.id, companyChannel.type);

                    return;
                }
            }

            // Otherwise they still need to ask the project owner.
            if (channels.length === 0) {
                ModalService.open({
                    title: 'Engineering Support',
                    children:
                        'You do not have permission to start a support channel. Contact one of the project owners for assistance.',
                });

                return;
            }

            if (channels.length === 1) {
                const [fallback] = channels;

                prefill();
                ChatService.activateChannel(fallback.id, fallback.type);

                return;
            }

            SidebarService.open(SidebarType.CHAT);
        }),

    openFeatureRequestChannel: async (initialMessage?: string | object) => {
        const project = currentProjectState.project!;

        const { channels } = await ChatService.getChannels(project.id);

        const existing = ChatHelpers.findDefaultChannel(channels, DiagramChatChannelType.FEATURE_REQUEST);

        // Like #engineering-support, team members share one #feature-request channel for the whole team.
        if (!existing && project.permissionsAs !== 'user') {
            return;
        }

        if (initialMessage) {
            ChatService.prefillMessage(initialMessage);
        }

        if (existing) {
            ChatService.activateChannel(existing.id, existing.type);

            return;
        }

        await SupportService.createDepSupportChannel(project.id, DiagramChatChannelType.FEATURE_REQUEST);
    },

    createDepSupportChannel: async (
        projectId: string,
        type: DepSupportChannelType,
        isCancelled: () => boolean = () => false,
    ) => {
        const channel = await ChatService.createChannel(projectId, ChatHelpers.defaults.channelNames[type], type);

        await ChatService.inviteEngineeringSupport(channel);

        if (isCancelled()) {
            return;
        }

        ChatService.activateChannel(channel.id, channel.type);
    },
};

const doc = (...content: JSONContent[]): JSONContent => ({ type: 'doc', content });

const paragraph = (...content: JSONContent[]): JSONContent => ({ type: 'paragraph', content });

const text = (value: string, bold = false): JSONContent => ({
    type: 'text',
    text: value,
    ...(bold && { marks: [{ type: 'bold' }] }),
});

export { SupportService };
